import {
  Component,
  NgModule,
  Output,
  Input,
  EventEmitter,
  ViewChild,
  ElementRef,
  AfterViewInit,
  OnDestroy,
  inject,
  OnInit,
  ChangeDetectionStrategy
} from '@angular/core';
import {
  DxTreeViewModule,
  DxTreeViewComponent,
  DxTreeViewTypes,
} from 'devextreme-angular/ui/tree-view';
import * as events from 'devextreme/events';
import { AuthPermissionService } from 'src/app/services/auth/permission.service';
import { navigation, NavigationItem } from '../../../app-navigation';
import { filterNavigation } from '../../../navigation-filter';
import { AuthService } from 'src/app/services/auth/auth.service';

@Component({
    selector: 'side-navigation-menu',
    templateUrl: './side-navigation-menu.component.html',
    styleUrls: ['./side-navigation-menu.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class SideNavigationMenuComponent
  implements AfterViewInit, OnDestroy, OnInit
{
  @ViewChild(DxTreeViewComponent, { static: true })
  menu!: DxTreeViewComponent;

  @Output()
  selectedItemChanged = new EventEmitter<DxTreeViewTypes.ItemClickEvent>();

  @Output()
  openMenu = new EventEmitter<any>();

  @Input()
  get compactMode() {
    return this._compactMode;
  }

  @Input()
  set selectedItem(value: String) {
    this._selectedItem = value;
    this.setSelectedItem();
  }

  get selectedItem(): String {
    return this._selectedItem;
  }

  set compactMode(val) {
    this._compactMode = val;

    if (!this.menu.instance) {
      return;
    }

    if (val) {
      this.menu.instance.collapseAll();
    }
  }

  private _selectedItem!: String;

  private _items!: Record<string, unknown>[];

  searchEditorOptions: any = {
    placeholder: 'Search modules',
    stylingMode: 'filled',
  };

  get items() {
    if (!this._items) {
      this._items = this.filterItemsBasedOnPermissions(navigation);
    }

    return this._items;
  }

  private _compactMode = false;

  constructor(
    private elementRef: ElementRef,
    private permissionsService: AuthPermissionService,
    private authService: AuthService
  ) {}

  ngOnInit(): void {
    this.permissionsService.getPermissions().subscribe(() => {
      this._items = this.filterItemsBasedOnPermissions(navigation);
    });
  }

  setSelectedItem() {
    if (!this.menu.instance) {
      return;
    }

    this.menu.instance.selectItem(this.selectedItem);
  }

  onItemClick(event: DxTreeViewTypes.ItemClickEvent) {
    this.selectedItemChanged.emit(event);
  }

  ngAfterViewInit() {
    this.setSelectedItem();
    events.on(this.elementRef.nativeElement, 'dxclick', (e: Event) => {
      this.openMenu.next(e);
    });
  }

  ngOnDestroy() {
    events.off(this.elementRef.nativeElement, 'dxclick');
  }

  private filterItemsBasedOnPermissions(items: NavigationItem[]): any[] {
    const visible = filterNavigation(items, {
      isGuest: this.authService.isUserGuest(),
      has: (permission) => this.permissionsService.hasPermission(permission),
    });
    return this.forTreeView(visible);
  }

  /** Collapsed, rooted paths; headings get no search text so a search never lists them. */
  private forTreeView(items: NavigationItem[]): any[] {
    return items.map((item) => ({
      ...item,
      expanded: false,
      searchText: item.heading ? '' : item.text,
      path: item.path && !/^\//.test(item.path) ? `/${item.path}` : item.path,
      items: item.items ? this.forTreeView(item.items) : undefined,
    }));
  }

  onItemExpanded(e: DxTreeViewTypes.ItemExpandedEvent) {
    const allNodes = this.flattenNodes(this.menu.instance.getNodes())
      .filter((node) => node.key !== e.node.key)
      .map((node) => {
        return {
          key: node.key,
          depthLevel: this.getDepthLevel(node),
        };
      });

    //get the level of depth of the node clicked (e)
    const depthLevel = this.getDepthLevel(e.node);

    allNodes.forEach((node) => {
      if (depthLevel == node.depthLevel) {
        e.component.collapseItem(node.key);
      }
    });
  }

  getDepthLevel(node: DxTreeViewTypes.Node<any>): number {
    let depthLevel = 0;
    while (node.parent) {
      depthLevel++;
      node = node.parent;
    }
    return depthLevel;
  }

  flattenNodes(
    nodes: DxTreeViewTypes.Node<any>[]
  ): DxTreeViewTypes.Node<any>[] {
    let result: DxTreeViewTypes.Node<any>[] = [];
    nodes.forEach((node) => {
      result.push(node);
      if (node.children) {
        result = result.concat(this.flattenNodes(node.children));
      }
    });
    return result;
  }
}

@NgModule({
  imports: [DxTreeViewModule],
  declarations: [SideNavigationMenuComponent],
  exports: [SideNavigationMenuComponent],
})
export class SideNavigationMenuModule {}
